import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

function CareNoteReviewView({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingNotes, setPendingNotes] = useState([]);
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    note: null,
    action: null,
    feedback: '',
    editedContent: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadPendingNotes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getPendingCareNotes(token);
      setPendingNotes(data.pendingNotes);
    } catch (err) {
      console.error('Error loading pending notes:', err);
      setError(err.response?.data?.msg || 'Failed to load pending care notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingNotes();
  }, [token]);

  const handleOpenReview = (note, action) => {
    setReviewDialog({
      open: true,
      note,
      action,
      feedback: '',
      editedContent: note.note.content
    });
  };

  const handleCloseReview = () => {
    setReviewDialog({
      open: false,
      note: null,
      action: null,
      feedback: '',
      editedContent: ''
    });
  };

  const handleSubmitReview = async () => {
    try {
      setSubmitting(true);
      setError('');

      const { note, action, feedback, editedContent } = reviewDialog;
      const approved = action === 'approve';

      await clinicalSupervisorApi.reviewCareNote(
        note.consultationId,
        note.noteId,
        approved,
        feedback,
        editedContent !== note.note.content ? editedContent : null,
        token
      );

      setSuccess(`Care note ${approved ? 'approved' : 'rejected'} successfully`);
      handleCloseReview();
      loadPendingNotes();
    } catch (err) {
      console.error('Error reviewing care note:', err);
      setError(err.response?.data?.msg || 'Failed to review care note');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/clinical-supervisor/dashboard')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5">Pending Care Note Reviews</Typography>
        <Chip label={`${pendingNotes.length} Pending`} color="warning" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Pending Notes Grid */}
      <Grid container spacing={2}>
        {pendingNotes.length > 0 ? (
          pendingNotes.map((item) => (
            <Grid item xs={12} key={`${item.consultationId}-${item.noteId}`}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {item.client.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Therapist: {item.therapist.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Consultation Date: {new Date(item.consultationDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip label={item.note.type} size="small" />
                      <Chip
                        label={item.note.priority}
                        size="small"
                        color={
                          item.note.priority === 'high'
                            ? 'error'
                            : item.note.priority === 'medium'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body1" paragraph>
                    {item.note.content}
                  </Typography>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    {item.note.applicableToAI && (
                      <Chip label="For AI" size="small" variant="outlined" />
                    )}
                    {item.note.applicableToHuman && (
                      <Chip label="For Human Therapist" size="small" variant="outlined" />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => handleOpenReview(item, 'reject')}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenReview(item, 'edit')}
                  >
                    Edit & Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => handleOpenReview(item, 'approve')}
                  >
                    Approve
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No pending care notes to review
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={handleCloseReview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'approve'
            ? 'Approve Care Note'
            : reviewDialog.action === 'edit'
            ? 'Edit and Approve Care Note'
            : 'Reject Care Note'}
          <IconButton
            onClick={handleCloseReview}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {reviewDialog.note && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Client: {reviewDialog.note.client.name}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Therapist: {reviewDialog.note.therapist.name}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {(reviewDialog.action === 'edit' || reviewDialog.action === 'approve') && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Care Note Content"
                  value={reviewDialog.editedContent}
                  onChange={(e) =>
                    setReviewDialog({ ...reviewDialog, editedContent: e.target.value })
                  }
                  margin="normal"
                  disabled={reviewDialog.action === 'approve'}
                />
              )}

              {reviewDialog.action === 'reject' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  value={reviewDialog.feedback}
                  onChange={(e) =>
                    setReviewDialog({ ...reviewDialog, feedback: e.target.value })
                  }
                  margin="normal"
                  required
                  helperText="Please provide feedback for the therapist"
                />
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseReview}>Cancel</Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            color={reviewDialog.action === 'reject' ? 'error' : 'success'}
            disabled={
              submitting ||
              (reviewDialog.action === 'reject' && !reviewDialog.feedback.trim())
            }
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : reviewDialog.action === 'reject' ? (
              'Reject'
            ) : (
              'Approve'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CareNoteReviewView;
